import { useCallback, useEffect, useRef, useState } from "react";

export default function usePictureInPicture() {
  const [pipActive, setPipActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const isPipSupported = typeof document !== "undefined" && "pictureInPictureEnabled" in document;

  const enterPip = useCallback(async (video?: HTMLVideoElement | null) => {
    const target = video || videoRef.current;
    if (!target || !isPipSupported) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      }
      await target.requestPictureInPicture();
      videoRef.current = target;
      setPipActive(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[PiP] Failed to enter PiP:", e);
    }
  }, [isPipSupported]);

  const exitPip = useCallback(async () => {
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      }
      setPipActive(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[PiP] Failed to exit PiP:", e);
    }
  }, []);

  const togglePip = useCallback(async (video?: HTMLVideoElement | null) => {
    if (pipActive) {
      await exitPip();
    } else {
      await enterPip(video);
    }
  }, [pipActive, enterPip, exitPip]);

  // PiP events fire on the video element, not document
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLeave = () => setPipActive(false);
    const handleEnter = () => setPipActive(true);

    video.addEventListener("leavepictureinpicture", handleLeave);
    video.addEventListener("enterpictureinpicture", handleEnter);

    return () => {
      video.removeEventListener("leavepictureinpicture", handleLeave);
      video.removeEventListener("enterpictureinpicture", handleEnter);
    };
  }, [pipActive]); // re-attach when videoRef changes via enterPip

  // Exit PiP on unmount
  useEffect(() => {
    return () => {
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(() => {});
      }
    };
  }, []);

  return {
    pipActive,
    isPipSupported,
    enterPip,
    exitPip,
    togglePip,
    videoRef,
  };
}
